import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  api,
  addDays,
  exportCsv,
  fmtPct,
  mondayOf,
  niceDate,
} from "@/lib/api";
import { Check } from "@/components/Check";
import { Progress } from "@/components/Progress";
import {
  ChevronLeft, ChevronRight, Download, Plus, Trash2,
  Clock, Flag, LayoutGrid, CalendarRange, RotateCcw,
  GripVertical, ArrowDownUp, CornerDownRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import useResizableColumns from "@/lib/useResizableColumns";
import AutoGrowInput from "@/components/AutoGrowInput";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const PRIORITIES = ["Low", "Medium", "High"];
const PRIO_COLORS = {
  Low: "text-muted-foreground border-border",
  Medium: "text-oud border-oud",
  High: "text-[hsl(var(--oxblood))] border-[hsl(var(--oxblood))]",
};

const SORT_MODES = ["manual", "priority", "time"];
const SORT_LABELS = { manual: "MANUAL", priority: "PRIORITY", time: "TIME" };
const PRIO_RANK = { High: 0, Medium: 1, Low: 2 };

function sortTasks(list, mode) {
  const arr = [...list];
  const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
  if (mode === "priority") {
    arr.sort((a, b) =>
      (PRIO_RANK[a.priority] ?? 3) - (PRIO_RANK[b.priority] ?? 3) || byOrder(a, b));
  } else if (mode === "time") {
    const key = (t) => (t.start_time ? t.start_time : "99:99");
    arr.sort((a, b) => key(a).localeCompare(key(b)) || byOrder(a, b));
  } else {
    arr.sort(byOrder);
  }
  return arr;
}

export default function WeeklyOS() {
  const [weekStart, setWeekStart] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [autoRollover, setAutoRollover] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("weekly-view") || "cards");
  const { setRef, Grip, reset, widths } = useResizableColumns(
    "weekly-days-v1", 7, { minWidth: 160 },
  );

  const load = useCallback(async (w, doRollover) => {
    // Silent rollover: bump any undone task from past days of THIS week to
    // today — only when the user has AUTO ROLLOVER enabled.
    if (doRollover) {
      try { await api.post(`/weekly-tasks/rollover?week_start=${w}`); } catch (_) { /* noop */ }
    }
    const t = await api.get("/weekly-tasks", { params: { week_start: w } }).then((r) => r.data);
    setTasks(Array.isArray(t) ? t : []);
  }, []);

  useEffect(() => {
    (async () => {
      const s = await api.get("/settings").then((r) => r.data);
      const roll = s.auto_rollover !== false;
      setAutoRollover(roll);
      setWeekStart(s.selected_week_start);
      await load(s.selected_week_start, roll);
    })();
  }, [load]);

  const toggleRollover = async () => {
    const next = !autoRollover;
    setAutoRollover(next);
    await api.put("/settings", { auto_rollover: next });
    if (next && weekStart) await load(weekStart, true);
  };

  const setWeekAndSave = async (w) => {
    setWeekStart(w);
    await api.put("/settings", { selected_week_start: w });
    await load(w, autoRollover);
  };

  const dayTasks = (day) => tasks.filter((t) => t.day === day);

  const { user } = useAuth();
  const isPro = !!user && user.plan !== "free";
  const [limitMsg, setLimitMsg] = useState(null);

  const addTask = async (day, title = "") => {
    try {
      const t = await api
        .post("/weekly-tasks", { week_start: weekStart, day, title })
        .then((r) => r.data);
      setTasks((prev) => [...prev, t]);
    } catch (e) {
      if (e.response?.status === 403) setLimitMsg(e.response?.data?.detail || "Upgrade to Pro for unlimited tasks");
    }
  };

  const updateTask = async (id, patch) => {
    const cur = tasks.find((t) => t.id === id);
    if (!cur) return;
    const next = { ...cur, ...patch };
    setTasks((prev) => prev.map((t) => (t.id === id ? next : t)));
    await api.put(`/weekly-tasks/${id}`, {
      week_start: next.week_start,
      day: next.day,
      title: next.title || "",
      done: !!next.done,
      order: next.order || 0,
      priority: next.priority ?? null,
      anytime: next.anytime ?? null,
      start_time: next.start_time ?? null,
      auto_rescheduled: next.auto_rescheduled ?? null,
    });
  };

  const deleteTask = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    await api.delete(`/weekly-tasks/${id}`);
  };

  const reorderDay = async (day, ids) => {
    setTasks((prev) =>
      prev.map((t) => {
        const idx = ids.indexOf(t.id);
        return idx === -1 ? t : { ...t, order: idx };
      }));
    await api.post("/weekly-tasks/reorder", { week_start: weekStart, day, ids });
  };

  const onExport = () => {
    const rows = tasks.map((t) => ({
      week_start: t.week_start,
      day: DAYS[t.day],
      title: t.title,
      done: t.done ? 1 : 0,
    }));
    exportCsv(`week-${weekStart}.csv`, rows);
  };

  if (!weekStart) return null;

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="weekly-os">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label">Weekly Brief</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            Week of {niceDate(weekStart)}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekAndSave(addDays(weekStart, -7))}
            className="hud-btn !px-2"
            data-testid="prev-week"
            aria-label="Previous week"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setWeekAndSave(mondayOf(new Date()))}
            className="hud-btn"
            data-testid="this-week"
          >
            THIS WEEK
          </button>
          <button
            onClick={() => setWeekAndSave(addDays(weekStart, 7))}
            className="hud-btn !px-2"
            data-testid="next-week"
            aria-label="Next week"
          >
            <ChevronRight size={14} />
          </button>
          <button
            onClick={onExport}
            className="hud-btn flex items-center gap-2"
            data-testid="export-week-csv"
          >
            <Download size={12} /> CSV
          </button>
          <div className="hud-btn !p-0 flex overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => { setViewMode("cards"); localStorage.setItem("weekly-view", "cards"); }}
              className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${viewMode === "cards" ? "bg-[hsl(var(--oud)/0.18)] text-oud" : "text-muted-foreground"}`}
              data-testid="view-cards"
            >
              <LayoutGrid size={11} /> CARDS
            </button>
            <button
              onClick={() => {
                if (!isPro) { setLimitMsg("Time-based scheduling is a Pro feature — upgrade to unlock the Timeline view"); return; }
                setViewMode("timeline"); localStorage.setItem("weekly-view", "timeline");
              }}
              className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${viewMode === "timeline" ? "bg-[hsl(var(--oud)/0.18)] text-oud" : "text-muted-foreground"} ${!isPro ? "opacity-50" : ""}`}
              data-testid="view-timeline"
            >
              <CalendarRange size={11} /> TIMELINE
            </button>
          </div>
          <button
            onClick={toggleRollover}
            className={`hud-btn !text-[10px] flex items-center gap-1.5 ${autoRollover ? "" : "opacity-60"}`}
            title="When ON, undone tasks from past days automatically move to today"
            data-testid="weekly-rollover-toggle"
          >
            <RotateCcw size={11} className={autoRollover ? "text-oud" : ""} />
            ROLLOVER {autoRollover ? "ON" : "OFF"}
          </button>
          {viewMode === "cards" && (
            <button onClick={reset} className="hud-btn !text-[10px]" title="Reset day widths" data-testid="weekly-reset-widths">
              RESET WIDTHS
            </button>
          )}
          {viewMode === "timeline" && (
            <Link to="/board" className="hud-btn !text-[10px]" data-testid="open-board">
              OPEN BOARD →
            </Link>
          )}
        </div>
      </header>

      {limitMsg && (
        <div className="hud-frame p-4 flex items-center justify-between gap-4" data-testid="weekly-limit-banner">
          <span className="mono text-[11px] uppercase tracking-widest text-[hsl(var(--oxblood))]">{limitMsg}</span>
          <div className="flex items-center gap-2">
            <Link to="/billing" className="hud-btn hud-btn-primary !text-[10px] whitespace-nowrap">UPGRADE</Link>
            <button onClick={() => setLimitMsg(null)} className="hud-btn !text-[10px]">DISMISS</button>
          </div>
        </div>
      )}

      {/* Mobile / tablet: 1 or 2 columns stacked. Desktop+: horizontal
          resizable row of day cards (Excel-style — drag right edge of any
          day to resize, double-click to auto-fit, persisted per-device). */}
      {viewMode === "cards" && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:hidden">
        {DAYS.map((label, i) => (
          <DayCard
            key={i}
            i={i}
            label={label}
            weekStart={weekStart}
            tasks={dayTasks(i)}
            todayIso={todayIso}
            addTask={addTask}
            updateTask={updateTask}
            deleteTask={deleteTask}
            reorderDay={reorderDay}
            isPro={isPro}
          />
        ))}
      </div>
      )}

      {viewMode === "cards" && (
      <div className="hidden lg:block overflow-x-auto">
        <div
          className="flex gap-3 items-stretch"
          style={{
            // If no manual widths set → fill the page (flex). Once user
            // drags any column, the row switches to its content width and
            // becomes horizontally scrollable.
            width: Object.keys(widths).length === 0 ? "100%" : "max-content",
            minWidth: "100%",
          }}
        >
          {DAYS.map((label, i) => {
            const hasManualWidth = widths[i] != null;
            const cardStyle = hasManualWidth
              ? { width: `${widths[i]}px`, minWidth: `${widths[i]}px`, flex: "0 0 auto", position: "relative" }
              : { flex: "1 1 0", minWidth: "160px", position: "relative" };
            return (
              <div key={i} ref={setRef(i)} style={cardStyle}>
                <DayCard
                  i={i}
                  label={label}
                  weekStart={weekStart}
                  tasks={dayTasks(i)}
                  todayIso={todayIso}
                  addTask={addTask}
                  updateTask={updateTask}
                  deleteTask={deleteTask}
                  reorderDay={reorderDay}
                  isPro={isPro}
                />
                <Grip index={i} />
              </div>
            );
          })}
        </div>
      </div>
      )}

      {viewMode === "timeline" && (
        <WeeklyTimeline
          weekStart={weekStart}
          tasks={tasks}
          todayIso={todayIso}
          updateTask={updateTask}
          deleteTask={deleteTask}
          addTask={addTask}
        />
      )}
    </div>
  );
}

/* ----------- Timeline view: 7 days × hourly slots, compact ------------- */

const HOURS = Array.from({ length: 10 }, (_, i) => 8 + i); // 8am → 5pm

function WeeklyTimeline({ weekStart, tasks, todayIso, updateTask, deleteTask, addTask }) {
  const timed = (t) => {
    if (!t.start_time) return null;
    const [h, m] = t.start_time.split(":").map(Number);
    return h + m / 60;
  };
  return (
    <div className="hud-frame overflow-x-auto">
      <div className="grid" style={{ gridTemplateColumns: "60px repeat(7, minmax(140px, 1fr))", minWidth: "100%" }}>
        <div />
        {DAYS.map((label, i) => {
          const date = addDays(weekStart, i);
          const isToday = date === todayIso;
          return (
            <div key={i} className="text-center py-3 border-l border-border" style={isToday ? { background: "hsl(var(--oxblood)/0.07)" } : null}>
              <div className="kpi-label">
                {label.slice(0, 3)}{isToday && <span className="text-[hsl(var(--oxblood))] ml-1">·</span>}
              </div>
              <div className="mono text-[10px] mt-1">
                {new Date(date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </div>
            </div>
          );
        })}
        {HOURS.map((hour) => (
          <React.Fragment key={hour}>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground text-right pr-2 pt-2 border-t border-border">
              {hour.toString().padStart(2, "0")}:00
            </div>
            {DAYS.map((_, di) => {
              const slotTasks = tasks.filter((t) => {
                if (t.day !== di) return false;
                const h = timed(t);
                return h !== null && h >= hour && h < hour + 1;
              });
              return (
                <div key={di} className="min-h-[58px] border-t border-l border-border/60 p-1.5 space-y-1">
                  {slotTasks.map((t) => (
                    <div key={t.id} className={`text-[11px] px-2 py-1 border-l-2 ${t.done ? "opacity-40 line-through" : ""} ${PRIO_COLORS[t.priority] || PRIO_COLORS.Low} bg-[hsl(var(--surface))]`}>
                      <div className="font-medium truncate">{t.title || "(untitled)"}</div>
                      <div className="mono text-[9px] text-muted-foreground tracking-widest">
                        {t.start_time}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        {/* "Anytime" row at the bottom — tasks without a time slot */}
        <div className="mono text-[10px] uppercase tracking-widest text-oud text-right pr-2 pt-2 border-t-2 border-oud/40">
          Anytime
        </div>
        {DAYS.map((_, di) => {
          const anytime = tasks.filter((t) => t.day === di && !t.start_time && (t.title || "").trim());
          return (
            <div key={di} className="min-h-[60px] border-t-2 border-oud/40 border-l border-border/60 p-1.5 space-y-1">
              {anytime.map((t) => (
                <div key={t.id} className={`text-[11px] px-2 py-1 border-l-2 ${t.done ? "opacity-40 line-through" : ""} ${PRIO_COLORS[t.priority] || PRIO_COLORS.Low} bg-[hsl(var(--surface))]`}>
                  <div className="font-medium truncate">{t.title}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-3 border-t border-border mono text-[10px] uppercase tracking-widest text-muted-foreground">
        To set a time on a task, switch back to CARDS view and click the time chip on the task. To drag tasks onto specific slots, use OPEN BOARD →
      </div>
    </div>
  );
}

function DayCard({ i, label, weekStart, tasks: dts, todayIso, addTask, updateTask, deleteTask, reorderDay, isPro }) {
  const date = addDays(weekStart, i);
  const done = dts.filter((t) => t.done).length;
  const total = dts.filter((t) => (t.title || "").trim()).length;
  const pct = total ? done / total : 0;
  const isToday = date === todayIso;

  const [sortMode, setSortMode] = useState(
    () => localStorage.getItem(`weekly-sort-${i}`) || "manual");
  const cycleSort = () => {
    const next = SORT_MODES[(SORT_MODES.indexOf(sortMode) + 1) % SORT_MODES.length];
    setSortMode(next);
    localStorage.setItem(`weekly-sort-${i}`, next);
  };
  const sorted = sortTasks(dts, sortMode);

  // Drag & drop (manual mode only). Rows become draggable only after the
  // user grabs the grip handle, so text inputs stay selectable.
  const canDrag = isPro && sortMode === "manual";
  const [armedId, setArmedId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const endDrag = () => { setArmedId(null); setDragId(null); setOverId(null); };
  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) { endDrag(); return; }
    const ids = sorted.map((t) => t.id);
    ids.splice(ids.indexOf(dragId), 1);
    const at = targetId === "__end" ? ids.length : ids.indexOf(targetId);
    ids.splice(at, 0, dragId);
    reorderDay(i, ids);
    endDrag();
  };

  return (
    <div
      className="hud-frame p-4 flex flex-col h-full"
      data-testid={`day-card-${i}`}
      style={isToday ? { borderColor: "hsl(var(--oxblood))" } : null}
    >
      <div className="flex items-baseline justify-between">
        <div className="min-w-0">
          <div className="kpi-label">
            {label.slice(0, 3).toUpperCase()}
            {isToday && (
              <span className="ml-2 text-[hsl(var(--oxblood))]">·TODAY</span>
            )}
          </div>
          <div className="display text-xl mt-0.5">
            {new Date(date + "T00:00:00").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isPro && (
          <button
            onClick={cycleSort}
            className={`mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 transition-colors ${
              sortMode === "manual"
                ? "text-muted-foreground/60 border-dashed border-border hover:text-foreground"
                : "text-oud border-oud/60"
            }`}
            title="Sort: Manual → Priority → Time"
            data-testid={`day-sort-${i}`}
          >
            <ArrowDownUp size={9} className="inline mr-0.5" />
            {SORT_LABELS[sortMode]}
          </button>
          )}
          <div className="mono text-[10px] text-muted-foreground tabular-nums">
            {done}/{total}
          </div>
        </div>
      </div>
      <div className="mt-2">
        <Progress value={pct} label={false} />
      </div>
      <div className="hud-divider my-3" />
      <div
        className="flex-1 space-y-1.5"
        onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverId("__end"); } }}
        onDrop={(e) => { if (dragId) { e.preventDefault(); dropOn("__end"); } }}
      >
        {sorted.map((t) => (
          <div
            key={t.id}
            className={`group ${dragId === t.id ? "opacity-40" : ""} ${
              overId === t.id ? "border-t-2 border-oud" : "border-t-2 border-transparent"
            }`}
            data-testid={`task-${t.id}`}
            draggable={canDrag && armedId === t.id}
            onDragStart={(e) => {
              setDragId(t.id);
              e.dataTransfer.effectAllowed = "move";
              try { e.dataTransfer.setData("text/plain", t.id); } catch (_) {}
            }}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              e.stopPropagation();
              setOverId(t.id);
            }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dropOn(t.id); }}
            onDragEnd={endDrag}
          >
            <div className="flex items-start gap-1.5">
              {canDrag && (
                <button
                  onMouseDown={() => setArmedId(t.id)}
                  onMouseUp={() => { if (!dragId) setArmedId(null); }}
                  onTouchStart={() => setArmedId(t.id)}
                  className="pt-1 flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/0 group-hover:text-muted-foreground/60 hover:!text-foreground transition-colors -ml-1"
                  title="Drag to reorder"
                  data-testid={`task-drag-${t.id}`}
                  aria-label="Drag to reorder"
                >
                  <GripVertical size={12} />
                </button>
              )}
              <div className="pt-1 flex-shrink-0">
                <Check
                  size="sm"
                  checked={t.done}
                  onChange={(v) => updateTask(t.id, { done: v })}
                  testid={`task-check-${t.id}`}
                />
              </div>
              <AutoGrowInput
                defaultValue={t.title}
                onBlur={(e) => {
                  if (e.target.value !== t.title) {
                    updateTask(t.id, { title: e.target.value });
                  }
                }}
                placeholder="Task…"
                className={`hud-input !border-transparent hover:!border-border !py-0.5 !text-sm flex-1 min-w-0 ${
                  t.done ? "line-through opacity-50" : ""
                }`}
                data-testid={`task-title-${t.id}`}
              />
              <button
                onClick={() => deleteTask(t.id)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(var(--signal))] transition-opacity pt-1 flex-shrink-0"
                data-testid={`task-delete-${t.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
            <div className={`flex items-center gap-1.5 mt-1 flex-wrap ${canDrag ? "ml-9" : "ml-6"}`}>
              {isPro && <TaskTimeChip task={t} updateTask={updateTask} />}
              {isPro && <TaskPriorityChip task={t} updateTask={updateTask} />}
              <TaskDayChip task={t} dayIdx={i} updateTask={updateTask} />
              {t.auto_rescheduled && !t.done && (
                <span
                  className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-oud border border-oud/40 px-1.5 py-0.5"
                  title="Rolled over from a previous day"
                >
                  <RotateCcw size={9} /> ROLLED
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() => addTask(i)}
        className="hud-btn w-full mt-3 !py-1.5 flex items-center justify-center gap-2"
        data-testid={`add-task-${i}`}
      >
        <Plus size={11} /> ADD TASK
      </button>
      <div className="mt-3 mono text-[10px] text-muted-foreground uppercase tracking-widest text-right">
        {fmtPct(pct)}
      </div>
    </div>
  );
}

/* ----------- Inline chips for priority + time ------------ */

function TaskDayChip({ task, dayIdx, updateTask }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const nextDay = (dayIdx + 1) % 7;
  const toggle = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setOpen((o) => !o);
  };
  const send = (d) => {
    setOpen(false);
    if (d === dayIdx) return;
    updateTask(task.id, { day: d, auto_rescheduled: false });
  };
  return (
    <span className="relative inline-flex">
      <button
        onClick={toggle}
        className="mono text-[9px] uppercase tracking-widest text-muted-foreground/50 hover:text-foreground border border-dashed border-border px-1.5 py-0.5"
        title="Send this task to another day"
        data-testid={`task-move-${task.id}`}
      >
        <CornerDownRight size={9} className="inline mr-0.5" /> MOVE
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[95] hud-frame bg-[hsl(var(--background))] p-1 flex flex-col min-w-[110px]"
            style={{ top: pos.top, left: pos.left }}
          >
            {DAYS.map((label, d) => (
              <button
                key={d}
                onClick={() => send(d)}
                disabled={d === dayIdx}
                className={`mono text-[9px] uppercase tracking-widest text-left px-2 py-1 transition-colors ${
                  d === dayIdx
                    ? "text-muted-foreground/30 cursor-default"
                    : "hover:bg-[hsl(var(--oud)/0.15)] hover:text-oud"
                }`}
                data-testid={`task-move-${task.id}-day-${d}`}
              >
                {label.slice(0, 3)}{d === nextDay ? " · NEXT" : ""}
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}

function TaskPriorityChip({ task, updateTask }) {
  const cycle = () => {
    const order = [null, "Low", "Medium", "High"];
    const idx = order.indexOf(task.priority ?? null);
    const next = order[(idx + 1) % order.length];
    updateTask(task.id, { priority: next });
  };
  if (!task.priority) {
    return (
      <button
        onClick={cycle}
        className="mono text-[9px] uppercase tracking-widest text-muted-foreground/50 hover:text-foreground border border-dashed border-border px-1.5 py-0.5"
        data-testid={`task-priority-${task.id}`}
      >
        <Flag size={9} className="inline mr-0.5" /> SET
      </button>
    );
  }
  return (
    <button
      onClick={cycle}
      className={`mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${PRIO_COLORS[task.priority]}`}
      data-testid={`task-priority-${task.id}`}
    >
      <Flag size={9} className="inline mr-0.5" />{task.priority.toUpperCase()}
    </button>
  );
}

function TaskTimeChip({ task, updateTask }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.start_time || "");

  const commit = () => {
    setEditing(false);
    const val = (draft || "").trim();
    if (!val) {
      // Empty → mark as anytime
      updateTask(task.id, { start_time: null, anytime: true });
      return;
    }
    // Accept HH:MM or H:MM
    const m = val.match(/^(\d{1,2}):?(\d{2})?$/);
    if (m) {
      const h = String(m[1]).padStart(2, "0");
      const mm = m[2] || "00";
      updateTask(task.id, { start_time: `${h}:${mm}`, anytime: false });
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setDraft(task.start_time || ""); setEditing(false); }
        }}
        placeholder="HH:MM"
        className="mono text-[10px] tracking-widest bg-transparent border border-oud px-1.5 py-0.5 w-20 outline-none"
        data-testid={`task-time-input-${task.id}`}
      />
    );
  }

  const display = task.start_time
    ? task.start_time
    : task.anytime
      ? "ANYTIME"
      : "SET TIME";
  const dim = !task.start_time && !task.anytime;
  return (
    <button
      onClick={() => { setDraft(task.start_time || ""); setEditing(true); }}
      className={`mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${dim ? "text-muted-foreground/50 border-dashed border-border" : "text-oud border-oud/60"}`}
      data-testid={`task-time-${task.id}`}
      title="Click to set a time, leave empty for 'Anytime'"
    >
      <Clock size={9} className="inline mr-0.5" />{display}
    </button>
  );
}
